import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useCare } from '../../contexts/CareContext'
import { useToast, humanizeError } from '../shared/Toast'
import { DAY_LABELS, DEFAULT_TEMPLATES } from '../../lib/tasks'
import { inviteMember, isEmail } from '../../lib/invite'

const CARE_STAGES = [
  { value: 'early', label: 'Early' },
  { value: 'middle', label: 'Middle' },
  { value: 'late', label: 'Late' },
]

const CAREGIVER_SHIFTS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
]

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7]

/** Comma- or newline-separated free text into a clean list. */
const splitList = (s) =>
  String(s || '')
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean)

/**
 * Two ways a coordinator adds a client: the full Ginie conversation, or this
 * form for when they already have the referral in front of them.
 *
 * Everything past the basics is optional and collapsed by default — the form
 * would otherwise be a wall on a phone. Each optional step is best effort: a
 * failed invite or care-plan import never loses the client record.
 */
export default function QuickAddClientSheet({ onCancel, onDone }) {
  const { user, profile } = useAuth()
  const { reload, setActiveId } = useCare()
  const toast = useToast()
  const navigate = useNavigate()

  const [mode, setMode] = useState(null) // null | 'quick'
  const [open, setOpen] = useState({ plan: false, family: false, caregiver: false, tasks: false })
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }))

  const [f, setF] = useState({
    full_name: '', preferred_name: '', age: '',
    conditions: '', medications: '', care_stage: 'unknown',
    care_plan: '',
    family_name: '', family_email: '',
    caregiver_name: '', caregiver_email: '',
    caregiver_shift: 'morning', caregiver_days: [...ALL_DAYS],
  })
  const [tasks, setTasks] = useState([]) // titles
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState('')

  const set = (k, v) => setF((c) => ({ ...c, [k]: v }))
  const toggleDay = (d) =>
    set('caregiver_days', f.caregiver_days.includes(d)
      ? f.caregiver_days.filter((x) => x !== d)
      : [...f.caregiver_days, d])
  const toggleTask = (t) =>
    setTasks((l) => (l.includes(t) ? l.filter((x) => x !== t) : [...l, t]))

  async function save() {
    if (!f.full_name.trim()) return toast.error("Enter the client's name.")
    if (f.family_email.trim() && !isEmail(f.family_email)) {
      return toast.error('That family email does not look right.')
    }
    if (f.caregiver_email.trim() && !isEmail(f.caregiver_email)) {
      return toast.error('That caregiver email does not look right.')
    }

    setBusy(true)
    try {
      setStep('Creating client…')

      // care_recipients stores date_of_birth, not age. An age gives only the
      // birth year, so this records 1 January of that year as a placeholder —
      // the real date can be set on the profile later.
      let dob = null
      const age = parseInt(f.age, 10)
      if (Number.isFinite(age) && age > 0 && age < 130) {
        dob = `${new Date().getFullYear() - age}-01-01`
      }

      const { data: created, error } = await supabase
        .from('care_recipients')
        .insert({
          created_by: user.id,
          full_name: f.full_name.trim(),
          preferred_name: f.preferred_name.trim() || null,
          date_of_birth: dob,
          care_stage: f.care_stage,
          conditions: splitList(f.conditions),
        })
        .select()
        .single()
      if (error) throw error

      const clientName = created.preferred_name || created.full_name

      // ── Medications ────────────────────────────────────────
      const meds = splitList(f.medications)
      if (meds.length) {
        const { error: medErr } = await supabase.from('medications').insert(
          meds.map((name) => ({ care_recipient_id: created.id, name, is_active: true }))
        )
        if (medErr) toast.error('Client added, but the medications could not be saved.')
      }

      // ── Care plan import (background) ──────────────────────
      // Deliberately not awaited: reading a long plan takes many seconds and
      // must not hold up the form. The toasts report it independently.
      if (f.care_plan.trim()) {
        toast.success('Care plan is being imported…')
        importCarePlanInBackground(created.id, f.care_plan, user.id, toast)
      }

      // ── Invites ───────────────────────────────────────────
      const wantsFamily = f.family_name.trim() && f.family_email.trim()
      const wantsCaregiver = f.caregiver_name.trim() && f.caregiver_email.trim()
      if (wantsFamily || wantsCaregiver) setStep('Sending invites…')

      if (wantsFamily) {
        const res = await inviteMember({
          recipientId: created.id, recipientName: clientName,
          name: f.family_name, email: f.family_email, role: 'family_member',
          inviterName: profile?.full_name || 'A coordinator',
        })
        if (!res.ok) toast.error('Client added, but the family invite could not be created.')
      }

      if (wantsCaregiver) {
        const res = await inviteMember({
          recipientId: created.id, recipientName: clientName,
          name: f.caregiver_name, email: f.caregiver_email, role: 'caregiver',
          relationship: `${f.caregiver_shift} caregiver`,
          inviterName: profile?.full_name || 'A coordinator',
        })
        if (!res.ok) toast.error('Client added, but the caregiver invite could not be created.')
      }

      // ── Caregiver task schedule ───────────────────────────
      if (tasks.length) {
        setStep('Setting up tasks…')
        const days = f.caregiver_days.length ? [...f.caregiver_days].sort((a, b) => a - b) : ALL_DAYS
        const { error: tplErr } = await supabase.from('task_templates').insert(
          tasks.map((title, i) => ({
            care_recipient_id: created.id,
            created_by: user.id,
            title,
            shift: f.caregiver_shift,
            days_of_week: days,
            assigned_role: 'caregiver',
            sort_order: i,
            is_active: true,
          }))
        )
        if (tplErr) toast.error('Client added, but the task schedule could not be saved.')
      }

      setStep('Done!')
      await reload()
      setActiveId(created.id)
      toast.success(`${clientName} added.`)
      onDone?.()
      navigate('/')
    } catch (err) {
      toast.error(humanizeError(err))
      setStep('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>
            {mode === 'quick' ? 'Quick add' : 'Add client'}
          </h2>
          <button onClick={onCancel} aria-label="Close" disabled={busy} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>

        {mode === null ? (
          <div className="stack" style={{ gap: 10 }}>
            <button className="card card-tap" onClick={() => navigate('/onboarding')} style={{ textAlign: 'left', width: '100%' }}>
              <div className="row" style={{ gap: 10 }}>
                <span style={{ fontSize: 22 }} aria-hidden>✨</span>
                <div className="grow">
                  <div style={{ fontWeight: 700 }}>Chat with Ginie</div>
                  <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    Answer a few questions and get a care plan built for you.
                  </div>
                </div>
                <span className="muted" aria-hidden>→</span>
              </div>
            </button>

            <button className="card card-tap" onClick={() => setMode('quick')} style={{ textAlign: 'left', width: '100%' }}>
              <div className="row" style={{ gap: 10 }}>
                <span style={{ fontSize: 22 }} aria-hidden>⚡</span>
                <div className="grow">
                  <div style={{ fontWeight: 700 }}>Quick add</div>
                  <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    Straight to a form — set up the whole client in one go.
                  </div>
                </div>
                <span className="muted" aria-hidden>→</span>
              </div>
            </button>
          </div>
        ) : (
          <div className="stack">
            {/* ── Basics ─────────────────────────────────────── */}
            <div className="field">
              <label className="label" htmlFor="qa-name">Client name</label>
              <input id="qa-name" className="input" value={f.full_name}
                onChange={(e) => set('full_name', e.target.value)} placeholder="e.g. Mohinder Kaur" />
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div className="field grow">
                <label className="label" htmlFor="qa-pref">
                  Preferred name <span className="hint">— optional</span>
                </label>
                <input id="qa-pref" className="input" value={f.preferred_name}
                  onChange={(e) => set('preferred_name', e.target.value)} placeholder="e.g. Ma" />
              </div>
              <div className="field" style={{ width: 92 }}>
                <label className="label" htmlFor="qa-age">Age</label>
                <input id="qa-age" className="input" type="number" inputMode="numeric"
                  value={f.age} onChange={(e) => set('age', e.target.value)} placeholder="78" />
              </div>
            </div>

            <div className="field">
              <span className="label">Care stage</span>
              <div className="row wrap" style={{ gap: 8 }}>
                {CARE_STAGES.map((s) => (
                  <button key={s.value} type="button"
                    className={`chip${f.care_stage === s.value ? ' chip-on' : ''}`}
                    aria-pressed={f.care_stage === s.value}
                    onClick={() => set('care_stage', f.care_stage === s.value ? 'unknown' : s.value)}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label className="label" htmlFor="qa-cond">
                Conditions <span className="hint">— comma separated</span>
              </label>
              <textarea id="qa-cond" className="textarea" style={{ minHeight: 72 }}
                value={f.conditions} onChange={(e) => set('conditions', e.target.value)}
                placeholder="COPD, hypertension, fall risk" />
            </div>

            <div className="field">
              <label className="label" htmlFor="qa-meds">
                Medications <span className="hint">— one per line</span>
              </label>
              <textarea id="qa-meds" className="textarea" style={{ minHeight: 72 }}
                value={f.medications} onChange={(e) => set('medications', e.target.value)}
                placeholder={'Ramipril 10mg\nAmlodipine 5mg'} />
              <span className="hint">Doses and timing can be filled in on the Medications screen.</span>
            </div>

            {/* ── Existing care plan ─────────────────────────── */}
            <Section
              title="Existing care plan"
              hint={f.care_plan.trim() ? 'Ready to import' : 'Optional'}
              open={open.plan} onToggle={() => toggle('plan')}
            >
              <div className="field">
                <label className="label" htmlFor="qa-plan">Paste existing care plan — optional</label>
                <textarea id="qa-plan" className="textarea" style={{ minHeight: 120 }}
                  value={f.care_plan} onChange={(e) => set('care_plan', e.target.value)}
                  placeholder="Paste care plan, caregiver instructions, or any notes about this client..." />
                <span className="hint">
                  Ginie reads this after the client is created and turns it into goals
                  and actions. You can review and edit them in Goals → Care Plan.
                </span>
              </div>
            </Section>

            {/* ── Family invite ─────────────────────────────── */}
            <Section
              title="Invite family member"
              hint={f.family_email.trim() ? f.family_email.trim() : 'Optional'}
              open={open.family} onToggle={() => toggle('family')}
            >
              <div className="field">
                <label className="label" htmlFor="qa-fam-name">Name</label>
                <input id="qa-fam-name" className="input" value={f.family_name}
                  onChange={(e) => set('family_name', e.target.value)} placeholder="e.g. Gurjot" />
              </div>
              <div className="field">
                <label className="label" htmlFor="qa-fam-email">Email</label>
                <input id="qa-fam-email" className="input" type="email" inputMode="email"
                  value={f.family_email} onChange={(e) => set('family_email', e.target.value)}
                  placeholder="name@example.com" />
              </div>
              <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                They&rsquo;ll get an invite email and can see everything in the app.
              </p>
            </Section>

            {/* ── Caregiver invite ──────────────────────────── */}
            <Section
              title="Invite caregiver"
              hint={f.caregiver_email.trim() ? f.caregiver_email.trim() : 'Optional'}
              open={open.caregiver} onToggle={() => toggle('caregiver')}
            >
              <div className="field">
                <label className="label" htmlFor="qa-cg-name">Name</label>
                <input id="qa-cg-name" className="input" value={f.caregiver_name}
                  onChange={(e) => set('caregiver_name', e.target.value)} placeholder="e.g. Priya" />
              </div>
              <div className="field">
                <label className="label" htmlFor="qa-cg-email">Email</label>
                <input id="qa-cg-email" className="input" type="email" inputMode="email"
                  value={f.caregiver_email} onChange={(e) => set('caregiver_email', e.target.value)}
                  placeholder="name@example.com" />
              </div>

              <div className="field">
                <span className="label">Shift</span>
                <div className="row wrap" style={{ gap: 8 }}>
                  {CAREGIVER_SHIFTS.map((s) => (
                    <button key={s.value} type="button"
                      className={`chip${f.caregiver_shift === s.value ? ' chip-on' : ''}`}
                      aria-pressed={f.caregiver_shift === s.value}
                      onClick={() => set('caregiver_shift', s.value)}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span className="label">Days</span>
                <div className="row" style={{ gap: 6 }}>
                  {DAY_LABELS.map((d) => {
                    const on = f.caregiver_days.includes(d.value)
                    return (
                      <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                        aria-pressed={on} aria-label={d.label}
                        style={{
                          width: 36, height: 36, borderRadius: '50%', fontWeight: 700, fontSize: 13,
                          border: on ? '2px solid var(--teal)' : '1.5px solid var(--border)',
                          background: on ? 'var(--teal-light)' : 'var(--surface)',
                          color: on ? 'var(--teal-dark)' : 'var(--muted)', cursor: 'pointer',
                        }}>
                        {d.short}
                      </button>
                    )
                  })}
                </div>
              </div>

              <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                They&rsquo;ll get a simplified view with just their tasks and shift log.
              </p>
            </Section>

            {/* ── Caregiver tasks ───────────────────────────── */}
            <Section
              title="Caregiver tasks"
              hint={tasks.length ? `${tasks.length} selected` : 'Optional'}
              open={open.tasks} onToggle={() => toggle('tasks')}
            >
              <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                Tap the tasks that apply. Each is scheduled for the{' '}
                <strong>{CAREGIVER_SHIFTS.find((s) => s.value === f.caregiver_shift)?.label.toLowerCase()}</strong>{' '}
                shift on the days selected above — both editable later.
              </p>
              <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
                {DEFAULT_TEMPLATES.map((t) => {
                  const on = tasks.includes(t.title)
                  return (
                    <button key={t.title} type="button"
                      className={`chip${on ? ' chip-on' : ''}`} aria-pressed={on}
                      onClick={() => toggleTask(t.title)}>
                      {t.title}
                    </button>
                  )
                })}
              </div>
            </Section>

            {busy && step && (
              <div className="row" style={{ gap: 8 }} role="status" aria-live="polite">
                <span className="spinner" />
                <span className="muted" style={{ fontSize: 14 }}>{step}</span>
              </div>
            )}

            <div className="row" style={{ gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setMode(null)} disabled={busy}>Back</button>
              <button className="btn btn-lg grow" onClick={save} disabled={busy || !f.full_name.trim()}>
                {busy ? <span className="spinner" /> : 'Add client'}
              </button>
            </div>
          </div>
        )}
        <div style={{ height: 8 }} />
      </div>
    </div>
  )
}

/** Collapsible block — the form is long, and most of it is skippable. */
function Section({ title, hint, open, onToggle, children }) {
  return (
    <div className="card stack" style={{ gap: 10, padding: 0, overflow: 'hidden' }}>
      <button
        type="button" onClick={onToggle} aria-expanded={open}
        style={{ width: '100%', background: 'none', border: 'none', padding: 14, textAlign: 'left', cursor: 'pointer' }}
      >
        <div className="spread">
          <span style={{ fontWeight: 700 }}>{title}</span>
          <span className="row" style={{ gap: 8 }}>
            <span className="muted" style={{ fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {hint}
            </span>
            <span className="muted" aria-hidden>{open ? '▾' : '▸'}</span>
          </span>
        </div>
      </button>
      {open && (
        <div className="stack" style={{ gap: 10, padding: '0 14px 14px' }}>
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * Fire-and-forget care-plan import. Runs after the client exists so the
 * membership check in ginie-chat passes, and reports through toasts rather
 * than holding the form open.
 */
async function importCarePlanInBackground(recipientId, text, userId, toast) {
  try {
    const { data, error } = await supabase.functions.invoke('ginie-chat', {
      body: { mode: 'import_care_plan', message: text, care_recipient_id: recipientId },
    })
    if (error) throw error
    if (data?.error) throw new Error(data.error)

    const goals = (data?.goals || []).filter((g) => g?.title)
    if (!goals.length) {
      toast.error('Ginie could not find any goals in that care plan.')
      return
    }

    const bullets = goals
      .map((g) => `• [${g.priority || 'ongoing'}] ${g.title}${g.description ? ` — ${g.description}` : ''}`)
      .join('\n')

    const { data: plan, error: planErr } = await supabase
      .from('care_plans')
      .insert({
        care_recipient_id: recipientId,
        created_by: userId,
        current_priorities: bullets,
        whats_not_working: data?.what_to_watch || null,
        whats_working: [
          data?.conditions_summary && `Conditions: ${data.conditions_summary}`,
          data?.routine_notes && `Routine: ${data.routine_notes}`,
        ].filter(Boolean).join('\n\n') || null,
        is_active: true,
      })
      .select('id')
      .single()
    if (planErr) throw planErr

    const rows = goals.flatMap((g, gi) =>
      (g.actions || []).filter((a) => a?.title).map((a, ai) => ({
        care_recipient_id: recipientId,
        care_plan_id: plan.id,
        goal_reference: g.title,
        title: a.title,
        description: a.notes || null,
        sort_order: gi * 100 + ai,
        created_by: userId,
      }))
    )
    if (rows.length) await supabase.from('care_plan_actions').insert(rows)

    toast.success('Care plan imported ✓')
  } catch (err) {
    toast.error(`Care plan could not be imported: ${humanizeError(err)}`)
  }
}
